import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useEditable } from '../context/EditableContext';
import InlineEditor from './InlineEditor';

const AdminSection = () => {
  const { isEditMode, adminCards, setAdminCards } = useEditable();
  const [editingCard, setEditingCard] = useState(null);
  const [editPosition, setEditPosition] = useState({ top: 0, left: 0 });

  const isExternalLink = (href) => href.startsWith('http');

  const handleCardEdit = (e, card) => {
    if (!isEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setEditPosition({ top: rect.top, left: rect.right + 10 });
    setEditingCard(card);
  };

  const handleCardSave = (updatedData) => {
    setAdminCards(prev => 
      prev.map(card => card.id === editingCard.id ? { ...card, ...updatedData } : card)
    );
    setEditingCard(null);
  };

  return (
    <section className="bg-white py-10 sm:py-16 px-3 sm:px-4">
      <div className="max-w-6xl mx-auto">
        {/* Section Title */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-center text-gray-800 tracking-wide mb-8 sm:mb-12 md:mb-16">
          ADMINISTRATION
        </h2>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {adminCards.map((card) => {
            const CardWrapper = isExternalLink(card.href) ? 'a' : Link;
            const linkProps = isExternalLink(card.href)
              ? { href: card.href, target: "_blank", rel: "noopener noreferrer" }
              : { to: card.href };

            return (
              <div key={card.id} className="relative group">
                {isEditMode && (
                  <button
                    onClick={(e) => handleCardEdit(e, card)}
                    className="absolute top-2 right-2 bg-green-500 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-20"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                <CardWrapper
                  {...linkProps}
                  onClick={(e) => isEditMode && e.preventDefault()}
                  className={`group block ${isEditMode ? 'ring-2 ring-transparent hover:ring-green-400 rounded-lg' : ''}`}
                >
                  <div className="relative overflow-hidden rounded-lg shadow-md transition-all duration-300 group-hover:shadow-xl group-hover:scale-[1.02]">
                    {/* Card Image */}
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    
                    {/* Overlay on hover */}
                    <div className="absolute inset-0 bg-[#5f7c8a]/0 group-hover:bg-[#5f7c8a]/20 transition-colors duration-300" />
                  </div>
                  
                  {/* Card Title */}
                  <p className="mt-3 sm:mt-4 text-center text-sm sm:text-base text-[#5f7c8a] font-medium tracking-wide group-hover:text-[#4a6470] transition-colors">
                    {card.title}
                  </p>
                </CardWrapper>
              </div>
            );
          })}
        </div>
      </div>

      {/* Inline Editor */}
      {editingCard && (
        <InlineEditor
          type="card"
          data={editingCard}
          onSave={handleCardSave}
          onClose={() => setEditingCard(null)}
          position={editPosition}
        />
      )}
    </section>
  );
};

export default AdminSection;
